import { formatDistanceToNow } from 'date-fns';
import { enUS, tr } from 'date-fns/locale';
import {
    resolvePageKeyFromNotificationI18n,
    resolvePageKeyFromNotificationTitle,
} from './notification-i18n-page-map.js';

const RECORD_NAME_KEYS = ['name', 'code', 'referenceNumber', 'userName'];
const ACTOR_EMAIL_RE = /^[^\s@]+@[^\s@]+$/;
const ACTOR_OBJECT_ID_RE = /^[a-f\d]{24}$/i;
const ACTOR_UUID_RE = /^[a-f\d]{8}-[a-f\d]{4}-[1-5][a-f\d]{3}-[89ab][a-f\d]{3}-[a-f\d]{12}$/i;

const MODULE_BY_EVENT_PREFIX = Object.freeze({
    ticket: 'ticket',
    formTask: 'form',
    asset: 'asset',
    assetCategory: 'asset',
    assetGroup: 'asset',
    assetDisposal: 'asset',
    assetLicense: 'asset',
    incident: 'incident',
    action: 'incident',
    document: 'document',
    dataBreach: 'dataBreach',
    gdprDataBreach: 'dataBreach',
    riskTreatment: 'risk',
    riskRegister: 'risk',
    riskMitigationDefinition: 'risk',
    changeRequest: 'changeRequest',
    user: 'user',
    control: 'control',
    workflowTask: 'workflow',
    workflowInformed: 'workflow',
    correctiveAction: 'capa',
    explicitConsent: 'consent',
    consentRecord: 'consent',
    applicationTracking: 'kvkk',
    dataSubjectRequest: 'kvkk',
    gdprDataSubjectRequest: 'kvkk',
    dataProcessingAgreement: 'kvkk',
    disposalRun: 'kvkk',
    dpiaAssessment: 'kvkk',
    gdprPrivacyNotice: 'kvkk',
    gdprCookieInventory: 'kvkk',
    cookieInventory: 'kvkk',
    exercise: 'bcm',
    crisisEvent: 'bcm',
    recoveryResources: 'bcm',
    RecoveryResources: 'bcm',
    bcp: 'bcm',
    bia: 'bcm',
    bcmInitialResponseLibrary: 'bcm',
    bcpLocation: 'bcm',
});

const MODULE_BY_PAGE_KEY = Object.freeze({
    'ticketing.ticket': 'ticket',
    ticketing: 'ticket',
    assetInventoryAssets: 'asset',
    assetInventoryAssetCategory: 'asset',
    assetInventoryAssetGroup: 'asset',
    incidentManagementIncidents: 'incident',
    incidentIncidents: 'incident',
    incidentActions: 'incident',
    reportDocument: 'document',
    gdprDataBreach: 'dataBreach',
    myWork: 'workflow',
    workflow: 'workflow',
    riskOperationsTreatment: 'risk',
    riskOperationsRiskRegister: 'risk',
    itsmChangeRequest: 'changeRequest',
    userOperationsUserManagement: 'user',
    internalControlControl: 'control',
    auditComplianceCapa: 'capa',
    gdprObtainingExplicitConsent: 'consent',
    gdprConsentRecord: 'consent',
    bcmExercises: 'bcm',
    bcmCrisisEvent: 'bcm',
    bcmBcp: 'bcm',
    bcmBia: 'bcm',
});

function dateLocale(language) {
    return String(language || '').toLowerCase().startsWith('tr') ? tr : enUS;
}

export function resolveNotificationEventName(row) {
    return row?.eventName || row?.data?.i18n?.key || row?.params?.i18n?.key || '';
}
export function resolveNotificationActorDisplayName(row) {
    const actor = row?.data?.actor;
    if (!actor || typeof actor !== 'object' || Array.isArray(actor)) return '';
    const displayName = typeof actor.displayName === 'string'
        ? actor.displayName.trim()
        : '';
    if (!displayName || displayName === actor.id || displayName === '[object Object]'
        || ACTOR_EMAIL_RE.test(displayName)
        || ACTOR_OBJECT_ID_RE.test(displayName)
        || ACTOR_UUID_RE.test(displayName)) return '';
    return displayName;
}

export function isSystemNotificationActor(row) {
    return row?.data?.actor?.id === 'system'
        && resolveNotificationActorDisplayName(row) === 'System';
}

function notificationIdentityValues(row, params) {
    const data = row?.data || {};
    return new Set([
        params.resourceId,
        params.entityId,
        data.resourceId,
        data.entityId,
        data.i18n?.params?.resourceId,
        data.i18n?.params?.entityId,
        params.i18n?.params?.resourceId,
        params.i18n?.params?.entityId,
    ]
        .filter((value) => value != null && String(value).trim())
        .map((value) => String(value).trim()));
}

export function resolveNotificationRecordName(row) {
    const params = row?.params || {};
    const identityValues = notificationIdentityValues(row, params);
    for (const key of RECORD_NAME_KEYS) {
        const value = typeof params[key] === 'string' ? params[key].trim() : '';
        if (value && !identityValues.has(value)) return value;
    }
    return '';
}

export function shouldShowNotificationBody(title, message) {
    const resolvedTitle = String(title || '').trim();
    const resolvedMessage = String(message || '').trim();
    return Boolean(resolvedMessage) && resolvedMessage !== resolvedTitle;
}

export function buildNotificationInboxCopy(row, { title, message } = {}) {
    const resolvedTitle = String(title || '').trim();
    const resolvedMessage = String(message || '').trim();
    const recordName = resolveNotificationRecordName(row);
    const primary = recordName || resolvedTitle;
    const subtitle = recordName && resolvedTitle && resolvedTitle !== recordName
        ? resolvedTitle
        : '';
    const body = shouldShowNotificationBody(primary, resolvedMessage)
        && resolvedMessage !== subtitle
        ? resolvedMessage
        : '';
    return { primary, subtitle, body, recordName };
}

export function resolveNotificationPageKey(row) {
    return row?.params?.pageKey
        || row?.data?.pageKey
        || resolvePageKeyFromNotificationI18n(
            resolveNotificationEventName(row),
            row?.params?.category || row?.data?.category,
        )
        || resolvePageKeyFromNotificationTitle(row?.title)
        || '';
}

function moduleFromPageKeyHint(pageKey) {
    if (!pageKey) return '';
    if (MODULE_BY_PAGE_KEY[pageKey]) return MODULE_BY_PAGE_KEY[pageKey];
    if (pageKey.includes('ticket')) return 'ticket';
    if (pageKey.startsWith('asset')) return 'asset';
    if (pageKey.startsWith('incident')) return 'incident';
    if (pageKey.startsWith('risk')) return 'risk';
    if (pageKey.startsWith('bcm')) return 'bcm';
    if (pageKey.startsWith('gdpr') || pageKey.startsWith('kvkk')) return 'kvkk';
    return '';
}

export function resolveNotificationModuleKey(row) {
    const fromPage = moduleFromPageKeyHint(resolveNotificationPageKey(row));
    if (fromPage) return fromPage;
    const prefix = String(resolveNotificationEventName(row)).split('.')[0];
    return MODULE_BY_EVENT_PREFIX[prefix] || '';
}

export function localizeNotificationModuleLabel(row, t) {
    const moduleKey = resolveNotificationModuleKey(row);
    if (!moduleKey || typeof t !== 'function') return '';
    return t(`notifications.table.modules.${moduleKey}`, { defaultValue: '' });
}

export function formatNotificationAbsoluteTime(value, language) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString(language, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

export function formatNotificationRelativeTime(value, language) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return formatDistanceToNow(date, {
        addSuffix: true,
        locale: dateLocale(language),
    });
}
